import { ArrowRight, ArrowLeft, Shield, Lock, AlertCircle, TrendingUp, FileCheck, BarChart3, CheckCircle, Eye, Activity, Database, RefreshCw, AlertTriangle, Users, Globe, Radar, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import React, { useEffect, useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from "@/components/ui/card";
import SectionDivider from '@/components/SectionDivider';
import SEO from '@/components/SEO';

const SecurityAdvisoryServices = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState(0);

  const advisoryServices = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Governance Risk & Compliance",
      subtitle: "Comprehensive governance framework and compliance management",
      description: "Build robust security governance with expert guidance on policy development, regulatory compliance, and strategic risk management tailored to your industry requirements.",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      borderColor: "rgba(59, 130, 246, 0.3)",
      iconColor: "#3b82f6",
      capabilities: [
        {
          title: "Industry Compliance Reviews & Audits",
          description: "Sector-specific regulatory assessments for PCI DSS, HIPAA, and industry standards"
        },
        {
          title: "Regulatory Compliance Reviews & Audits",
          description: "GDPR, ISO 27001, and global data protection regulation compliance evaluations"
        },
        {
          title: "Virtual CISO (vCISO) Services",
          description: "Strategic cybersecurity leadership and program development guidance"
        },
        {
          title: "Risk Assessment",
          description: "Organizational risk identification, evaluation, and mitigation planning"
        },
        {
          title: "Policies and Procedures Development",
          description: "Customized cybersecurity policies aligned with regulatory requirements"
        }
      ]
    },
    {
      icon: <AlertTriangle className="w-8 h-8" />,
      title: "Vulnerability Management",
      subtitle: "Comprehensive vulnerability identification and remediation programs",
      description: "Establish mature vulnerability management programs with continuous scanning, risk-based prioritization, and strategic remediation planning to reduce your attack surface.",
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      borderColor: "rgba(239, 68, 68, 0.3)",
      iconColor: "#ef4444",
      capabilities: [
        {
          title: "Vulnerability Management Program Setup",
          description: "Repeatable vulnerability scanning, triage, and remediation process establishment"
        },
        {
          title: "Vulnerability Assessment",
          description: "Asset scanning for known vulnerabilities with actionable remediation insights"
        },
        {
          title: "Vulnerability Management Maturity Assessment",
          description: "Current VM program evaluation against 6-level maturity model"
        },
        {
          title: "Risk-Based Remediation Planning",
          description: "Vulnerability prioritization using asset classification and threat context"
        },
        {
          title: "Asset Classification & Risk Tiering",
          description: "IT asset categorization by criticality and exposure for SLA guidance"
        },
        {
          title: "Attack Surface Mapping",
          description: "Exposed and shadow infrastructure identification using OSINT techniques"
        },
        {
          title: "VM Metrics & Reporting Toolkit",
          description: "Dashboards, KPIs, and templates for VM effectiveness tracking"
        }
      ]
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Cyber Threat Intelligence",
      subtitle: "Proactive threat monitoring and intelligence gathering using OSINT",
      description: "Cost-effective threat intelligence services powered by open-source intelligence, providing early warning systems and threat actor analysis without vendor lock-in.",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      borderColor: "rgba(139, 92, 246, 0.3)",
      iconColor: "#8b5cf6",
      capabilities: [
        {
          title: "Threat Actor Impact Assessment",
          description: "Industry-specific threat actor analysis using public intelligence sources"
        },
        {
          title: "Sensitive Data Exposure Assessment",
          description: "Public repository scanning for leaked credentials and proprietary data"
        },
        {
          title: "High-Value Target (HVT) Profiling",
          description: "Executive risk assessment for phishing and impersonation threats"
        },
        {
          title: "CTI Maturity Assessment",
          description: "Cyber threat intelligence readiness and capability gap analysis"
        }
      ]
    }
  ];

  const futureServices = [
    {
      name: "Ransomware & Third-Party Impact Notifications",
      description: "Monitor ransomware group extortion sites and threat actor channels for references to your vendors, partners, or supply chain entities.",
      icon: AlertTriangle
    },
    {
      name: "Deep/Dark Web Surveillance",
      description: "Track relevant discussions, breached data, and threat mentions in underground forums, marketplaces, and hidden services.",
      icon: Globe
    },
    {
      name: "Threat Feed Integration & IOC Management",
      description: "Ingest and operationalize Indicators of Compromise (IOCs) from curated feeds, integrating them into SIEM, EDR, or TIP systems for proactive defense.",
      icon: Database
    },
    {
      name: "Brand Monitoring",
      description: "Track mentions of your company, domains, and products across public web, social media, and threat actor channels, detecting impersonation, spoofed domains, or reputational threats.",
      icon: Radar
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Cost-Effective Intelligence",
      description: "Early-stage threat intelligence using open-source tools without vendor lock-in",
      color: "#3b82f6"
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Scalable Architecture",
      description: "Roadmap to commercial CTI integration as your needs and budget evolve",
      color: "#ef4444"
    },
    {
      icon: <CheckCircle className="w-12 h-12" />,
      title: "Fast Deployment",
      description: "Quick implementation and operationalization without complex vendor integrations",
      color: "#8b5cf6"
    }
  ];

  return (
    <PageLayout>
      <SEO 
        title="Security Advisory Services | vCISO, Compliance Auditing & Risk Management"
        description="Expert security advisory services including virtual CISO (vCISO), compliance auditing, governance risk management, vulnerability assessment programs, and threat intelligence consulting. Build mature security programs with strategic guidance from certified professionals."
        keywords={[
          'security advisory services',
          'virtual CISO services',
          'vCISO consulting',
          'compliance auditing',
          'governance risk compliance',
          'vulnerability management program',
          'cybersecurity consulting',
          'risk management services',
          'threat intelligence consulting',
          'security governance framework',
          'regulatory compliance audit',
          'cybersecurity program development',
          'security risk assessment',
          'cyber threat intelligence',
          'security policy development'
        ]}
      />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 30%, rgba(167, 139, 250, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 80% 70%, rgba(192, 132, 252, 0.15) 0%, transparent 50%)" }} />
        </div>

        <div className="container mx-auto px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/services"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(167, 139, 250, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Services
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div
                className="inline-block mb-4 px-6 py-3 text-sm font-medium border shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)",
                  color: "#ffffff",
                  borderRadius: "999px",
                  borderColor: "rgba(167, 139, 250, 0.3)",
                  borderWidth: 1.5,
                  boxShadow: "0 4px 24px 0 rgba(167, 139, 250, 0.25)",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                <Lock className="w-4 h-4 inline mr-2" />
Security Advisory Services
              </div>
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#a78bfa",
                    fontWeight: 700,
                  }}
                >
                  Security Advisory Services
                </span>
                <span className="block mt-2" style={{ fontSize: "0.7em", color: "#e5e7eb" }}>
                  Strategic Security Guidance
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto"
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Strategic security advisory services to build mature security programs with expert guidance, 
                governance frameworks, and comprehensive risk management.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Core Services */}
      <section
        className="py-16 px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                style={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                Comprehensive Security Management
              </h2>
              <p
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                End-to-end security program management and risk reduction
              </p>
            </motion.div>

            {/* Service Navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {advisoryServices.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`px-3 py-4 rounded-lg font-medium transition-all duration-300 text-center ${
                    activeService === index ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    background: activeService === index ? service.gradient : 'rgba(15, 15, 23, 0.85)',
                    border: `1.5px solid ${activeService === index ? service.borderColor : 'rgba(255, 255, 255, 0.1)'}`,
                    color: "#fff",
                    minHeight: "100px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "16px 12px"
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    {service.icon}
                  </div>
                  <span className="text-xs sm:text-sm leading-tight" style={{ wordBreak: "break-word" }}>
                    {service.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Service Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <Card
                  className="mb-8"
                  style={{
                    background: "rgba(15, 15, 23, 0.85)",
                    border: `2px solid ${advisoryServices[activeService].borderColor}`,
                    borderRadius: 16,
                  }}
                >
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-8">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 flex-shrink-0"
                        style={{
                          width: 64,
                          height: 64,
                          background: advisoryServices[activeService].gradient,
                          borderRadius: 16,
                        }}
                      >
                        {advisoryServices[activeService].icon}
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl md:text-3xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          {advisoryServices[activeService].title}
                        </h3>
                        <p
                          className="text-base sm:text-lg"
                          style={{
                            color: advisoryServices[activeService].iconColor,
                            fontWeight: 600,
                            marginBottom: 16,
                          }}
                        >
                          {advisoryServices[activeService].subtitle}
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.7,
                          }}
                        >
                          {advisoryServices[activeService].description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {advisoryServices[activeService].capabilities.map((capability, idx) => (
                        <div
                          key={idx}
                          className="p-4 sm:p-6 rounded-lg"
                          style={{
                            background: "rgba(0, 0, 0, 0.3)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <div className="flex items-start">
                            <Target
                              className="w-5 h-5 mr-3 mt-1 flex-shrink-0"
                              style={{ color: advisoryServices[activeService].iconColor }}
                            />
                            <div className="flex-1">
                              <h4
                                className="text-base sm:text-lg"
                                style={{
                                  fontWeight: 600,
                                  color: "#fff",
                                  marginBottom: 8,
                                }}
                              >
                                {capability.title}
                              </h4>
                              <p
                                className="text-xs sm:text-sm"
                                style={{
                                  color: "#9ca3af",
                                  lineHeight: 1.6,
                                }}
                              >
                                {capability.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>


            {/* Benefits Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="flex items-center justify-center mx-auto mb-4" 
                    style={{ 
                      color: benefit.color,
                      width: "48px",
                      height: "48px"
                    }}
                  >
                    {benefit.icon}
                  </div>
                  <h4 style={{ fontWeight: 700, color: "#fff", marginBottom: 8, fontSize: 20 }}>
                    {benefit.title}
                  </h4>
                  <p style={{ color: "#9ca3af", fontSize: 15, lineHeight: 1.6 }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-16 text-center"
              style={{
                background: "rgba(15, 15, 23, 0.85)",
                borderRadius: 16,
                padding: "3rem",
                border: "1px solid rgba(167, 139, 250, 0.2)",
              }}
            >
              <h3
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: 16,
                }}
              >
                Build Your Security Program
              </h3>
              <p
                style={{
                  color: "#d1d5db",
                  fontSize: 18,
                  marginBottom: 32,
                  maxWidth: 600,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Let our experts help you build a mature security program that protects your business and ensures compliance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contactus"
                  className="inline-flex items-center font-medium transition-all shadow-lg hover:shadow-xl"
                  style={{
                    background: "linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)",
                    color: "#ffffff",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    boxShadow: "0 4px 24px 0 rgba(167, 139, 250, 0.25)",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #c084fc 0%, #e879f9 100%)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)";
                  }}
                >
                  Start Your Security Journey
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center font-medium transition-all"
                  style={{
                    background: "transparent",
                    color: "#a78bfa",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    border: "1.5px solid #a78bfa",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "rgba(167, 139, 250, 0.1)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  View All Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default SecurityAdvisoryServices;